import { type FC } from 'react';
import styled from 'styled-components';

import { type CoverManifest } from '@soomo/lib/types/WebtextManifest';

import { numberToOrdinalInWords } from '~/utils/helpers';
import { type PrintCoverConfig, type PrintCoverFrontConfig } from '~/types';

const COVER_WIDTH_PT = 612;
const COVER_HEIGHT_PT = 792;
// Convert to px for viewBox (1pt ~= 1.333px)
const COVER_WIDTH_PX = COVER_WIDTH_PT * (4 / 3);
const COVER_HEIGHT_PX = COVER_HEIGHT_PT * (4 / 3);

const StyledFrontCover = styled.div<PrintCoverFrontConfig>`
	position: relative;
	width: ${COVER_WIDTH_PT}pt;
	height: ${COVER_HEIGHT_PT}pt;
	transform: scale(0.75);
	color: ${({ text_color }) => text_color ?? '#FBFBFB'};
	background: ${({ url }) => `url('${url}') top right / cover no-repeat`};

	#editionfront {
		position: absolute;
		left: ${({ details_left_offset_points }) => 50 + (details_left_offset_points ?? 0)}pt;
		overflow: hidden;
		bottom: ${({ details_bottom_offset_points }) => 487 + (details_bottom_offset_points ?? 0)}pt;
		text-transform: uppercase;

		p {
			margin: 0;
			color: ${({ text_color }) => text_color};
			font: ${({ edition_font }) => edition_font ?? '15pt Helvetica, sans-serif'};
			text-align: left;
		}
	}

	#bordered-header {
		width: 105px;
		border-bottom: 5px solid ${({ bordered_header_color }) => bordered_header_color ?? '#fff'};
		display: block;
		position: absolute;
		bottom: ${({ details_bottom_offset_points }) => 472 + (details_bottom_offset_points ?? 0)}pt;
		left: ${({ details_left_offset_points }) => 50 + (details_left_offset_points ?? 0)}pt;
	}

	#author {
		position: absolute;
		left: ${({ details_left_offset_points }) => 50 + (details_left_offset_points ?? 0)}pt;
		overflow: hidden;
		bottom: ${({ author_top_offset_points, details_bottom_offset_points }) =>
			442 - (author_top_offset_points ?? 0) + (details_bottom_offset_points ?? 0)}pt;
		text-transform: uppercase;

		p {
			margin: 0;
			color: ${({ text_color }) => text_color};
			font: ${({ author_font }) => author_font ?? 'bold 15pt Helvetica, sans-serif'};
			text-align: left;
		}
	}

	#copyright {
		position: absolute;
		left: ${({ details_left_offset_points }) => 50 + (details_left_offset_points ?? 0)}pt;
		overflow: hidden;
		bottom: ${({ author_top_offset_points, details_bottom_offset_points }) =>
			427 - (author_top_offset_points ?? 0) + (details_bottom_offset_points ?? 0)}pt;
		text-transform: uppercase;

		p {
			margin: 0;
			color: ${({ text_color }) => text_color};
			font: ${({ copyright_font }) => copyright_font ?? '9pt Helvetica, sans-serif'};
			text-align: left;
		}
	}

	#courseinfo {
		position: absolute;
		right: 40pt;
		bottom: 10pt;
		height: 50pt;
		width: 445pt;

		p {
			margin: 0;
			color: white;
			font-size: 14pt;
			font-family: 'Helvetica Neue', Helvetica, sans-serif;
			text-align: right;
		}
	}
`;

interface Props {
	coverManifest?: CoverManifest;
	printCoverConfig?: PrintCoverConfig;
}

export const PrintPreview: FC<Props> = ({ coverManifest, printCoverConfig }) => {
	/**
	 * The likely case is there's an outstanding request for the signup config. If that request
	 * fails, the user will surely see an error page, but if for any reason they don't, we're
	 * OK with simply not rendering the preview.
	 */
	if (!coverManifest || !printCoverConfig) return null;

	const editionContent =
		coverManifest.book_edition_number != null
			? `${numberToOrdinalInWords(coverManifest.book_edition_number)} edition`
			: null;
	const author = coverManifest.book_author ?? null;
	const copyright = coverManifest.book_copyright_notice ?? null;
	const schoolName = printCoverConfig.school_name ?? null;

	return (
		<svg
			height="100%"
			width="100%"
			viewBox={`0 0 ${COVER_WIDTH_PX} ${COVER_HEIGHT_PX}`}
			role="img"
			aria-label="Print cover"
			preserveAspectRatio="xMidYMid meet">
			<foreignObject x={0} y={0} width={COVER_WIDTH_PX} height={COVER_HEIGHT_PX}>
				<StyledFrontCover
					// xmlns is required for Safari foreignObject support
					xmlns="http://www.w3.org/1999/xhtml"
					{...printCoverConfig.front}>
					{editionContent && (
						<div id="editionfront">
							<p dangerouslySetInnerHTML={{ __html: editionContent }} />
						</div>
					)}
					<div id="bordered-header" />
					{author && (
						<div id="author">
							<p dangerouslySetInnerHTML={{ __html: author }} />
						</div>
					)}
					{copyright && (
						<div id="copyright">
							<p dangerouslySetInnerHTML={{ __html: copyright }} />
						</div>
					)}
					{schoolName && (
						<div id="courseinfo">
							<p>{schoolName}</p>
						</div>
					)}
				</StyledFrontCover>
			</foreignObject>
		</svg>
	);
};
